import { useEffect, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { api, API } from "@/lib/api";
import { toast } from "sonner";
import { Download, ArrowLeft, CircleNotch, ChatCircleDots } from "@phosphor-icons/react";
import StatusBadge from "@/components/StatusBadge";
import ScoreRing from "@/components/ScoreRing";
import RelationshipGraph from "@/components/RelationshipGraph";
import CopilotChat from "@/components/CopilotChat";
import DossierSections from "@/components/DossierSections";
import OISCard from "@/components/OISCard";
import InvestmentCommitteeTab from "@/components/InvestmentCommitteeTab";
import DataSourcesSection from "@/components/DataSourcesSection";
import InstitutionalQualityPanel from "@/components/InstitutionalQualityPanel";

const SECTIONS = [
  { id: "overview", label: "Overview" },
  { id: "quality", label: "Quality & Confidence" },
  { id: "ois", label: "OIS™ Score" },
  { id: "committee", label: "Investment Committee" },
  { id: "thesis", label: "Thesis" },
  { id: "market", label: "Market" },
  { id: "regulatory", label: "Regulatory" },
  { id: "risk", label: "Risk" },
  { id: "esg", label: "ESG" },
  { id: "valuation", label: "Valuation" },
  { id: "monte_carlo", label: "Monte Carlo" },
  { id: "bayesian", label: "Bayesian" },
  { id: "ecosystem", label: "Ecosystem" },
  { id: "deals", label: "Deals" },
  { id: "structuring", label: "Structuring" },
  { id: "execution", label: "Execution" },
  { id: "sources", label: "Sources" },
];

export default function AssessmentView() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [a, setA] = useState(null);
  const [section, setSection] = useState("overview");
  const [copilotOpen, setCopilotOpen] = useState(false);
  const pollRef = useRef(null);

  const load = async () => {
    try {
      const { data } = await api.get(`/assessments/${id}`);
      setA(data);
      return data;
    } catch {
      toast.error("Not found");
      navigate("/assessments");
    }
  };

  useEffect(() => {
    load();
    pollRef.current = setInterval(async () => {
      const data = await load();
      if (data && (data.status === "completed" || data.status === "failed")) {
        clearInterval(pollRef.current);
      }
    }, 4000);
    return () => clearInterval(pollRef.current);
  }, [id]);

  if (!a) return <div className="p-8 sm:p-12 text-overline text-muted-foreground" data-testid="asm-loading">Loading…</div>;

  const isReady = a.status === "completed" && a.dossier;
  const memo = a.dossier?.memo || {};
  const scores = a.scores || memo.scores || {};

  const exportFormat = async (fmt) => {
    try {
      const token = localStorage.getItem("orixa_token") || "";
      const resp = await fetch(`${API}/assessments/${id}/export/${fmt}`, {
        credentials: "include",
        headers: token ? { Authorization: `Bearer ${token}` } : {},
      });
      if (!resp.ok) throw new Error();
      const blob = await resp.blob();
      const url = URL.createObjectURL(blob);
      const link = document.createElement("a");
      const ext = fmt === "pdf" ? "pdf" : fmt === "docx" ? "docx" : "pptx";
      const safeTitle = (a.title || "dossier").replace(/[^a-z0-9]+/gi, "-").slice(0, 60);
      link.href = url; link.download = `Orixa-Dossier-${safeTitle}.${ext}`;
      link.click();
      URL.revokeObjectURL(url);
      toast.success(`${ext.toUpperCase()} downloaded`);
    } catch { toast.error("Export failed"); }
  };

  return (
    <div className="p-4 sm:p-8 lg:p-12 max-w-7xl mx-auto" data-testid="assessment-view">
      <button onClick={() => navigate("/assessments")} className="text-sm text-muted-foreground hover:text-foreground inline-flex items-center gap-1 mb-4 sm:mb-6 font-medium" data-testid="back-to-assessments">
        <ArrowLeft size={14} /> Dossiers
      </button>

      {/* Header */}
      <div className="flex items-start justify-between flex-wrap gap-3">
        <div className="min-w-0 flex-1">
          <div className="text-overline text-primary mb-2 sm:mb-3">Origination dossier</div>
          <h1 className="font-display text-2xl sm:text-4xl lg:text-5xl tracking-tight leading-tight">{a.title}</h1>
          <div className="mt-3 flex flex-wrap items-center gap-x-3 gap-y-1.5 text-xs sm:text-sm text-muted-foreground">
            <span data-testid="asm-countries" className="font-medium">{a.countries.join(", ")}</span>
            <span className="h-3 w-px bg-border" />
            <span>{a.sector}</span>
            <span className="h-3 w-px bg-border" />
            <span className="font-mono">{a.currency || "EUR"} {a.budget_eur.toLocaleString()}</span>
            <span className="h-3 w-px bg-border" />
            <span className="hidden sm:inline">{a.horizon}</span>
            <span className="hidden sm:inline h-3 w-px bg-border" />
            <span className="hidden sm:inline">{a.objective}</span>
          </div>
        </div>
        <div className="flex items-center gap-2 sm:gap-3 flex-shrink-0">
          <StatusBadge status={a.status} />
          {isReady && (
            <div className="inline-flex items-center gap-1 sm:gap-1.5 border border-border bg-card rounded-lg overflow-hidden" data-testid="export-group">
              <button onClick={() => exportFormat("pdf")} className="inline-flex items-center gap-1.5 px-3 sm:px-4 py-2 text-xs sm:text-sm hover:bg-secondary transition-colors font-medium" data-testid="export-pdf">
                <Download size={14} /> PDF
              </button>
              <div className="h-5 w-px bg-border" />
              <button onClick={() => exportFormat("docx")} className="inline-flex items-center gap-1.5 px-3 sm:px-4 py-2 text-xs sm:text-sm hover:bg-secondary transition-colors font-medium" data-testid="export-docx">
                DOCX
              </button>
              <div className="h-5 w-px bg-border" />
              <button onClick={() => exportFormat("pptx")} className="inline-flex items-center gap-1.5 px-3 sm:px-4 py-2 text-xs sm:text-sm hover:bg-secondary transition-colors font-medium" data-testid="export-pptx">
                PPTX
              </button>
            </div>
          )}
        </div>
      </div>

      {/* Generation progress */}
      {!isReady && (
        <div className="mt-8 sm:mt-10 bg-card border border-border rounded-2xl p-5 sm:p-6 card-elev" data-testid="generation-progress">
          <div className="flex items-center gap-3 mb-4">
            <div className="h-10 w-10 rounded-lg bg-teal-soft text-teal grid place-items-center flex-shrink-0">
              <CircleNotch size={18} className="animate-spin" weight="bold" />
            </div>
            <div className="flex-1 min-w-0">
              <div className="font-display text-base sm:text-lg tracking-tight">Researching mandate</div>
              <div className="text-xs sm:text-sm text-muted-foreground truncate">{a.current_agent || "Initialising research engine…"}</div>
            </div>
            <div className="data-large text-xl sm:text-2xl text-primary">{a.progress || 0}%</div>
          </div>
          <div className="h-2 bg-secondary rounded-full overflow-hidden">
            <div className="h-full bg-gradient-to-r from-primary to-[hsl(var(--info))] transition-all duration-500 rounded-full" style={{ width: `${a.progress || 0}%` }} />
          </div>
          <div className="mt-4 text-xs sm:text-sm text-muted-foreground leading-relaxed">
            Researching markets, regulators, risks, ecosystems and deal pipeline across <span className="font-semibold text-foreground">{a.countries.join(", ")}</span>. Typical turnaround: 2–7 minutes.
          </div>
        </div>
      )}

      {isReady && (
        <div className="mt-8 sm:mt-10 grid grid-cols-1 lg:grid-cols-3 gap-6 sm:gap-8">
          <div className="lg:col-span-2 min-w-0">
            {/* Section tabs - horizontal scroll on mobile */}
            <div className="border-b border-border flex gap-1 overflow-x-auto scroll-hide -mx-4 px-4 sm:mx-0 sm:px-0" data-testid="section-tabs">
              {SECTIONS.map((s) => (
                <button key={s.id} onClick={() => setSection(s.id)} className={`px-3 sm:px-4 py-2.5 sm:py-3 text-sm whitespace-nowrap border-b-2 transition-colors -mb-px font-medium ${section === s.id ? "border-primary text-primary" : "border-transparent text-muted-foreground hover:text-foreground"}`} data-testid={`tab-${s.id}`}>
                  {s.label}
                </button>
              ))}
            </div>

            {/* Mobile scores panel summary */}
            <div className="lg:hidden mt-6 bg-card border border-border rounded-2xl p-5 card-elev" data-testid="scores-panel-mobile">
              <div className="text-overline text-muted-foreground mb-4">Opportunity Qualification</div>
              <div className="grid grid-cols-3 gap-3">
                <ScoreRing value={scores.opportunity_score} label="Opportunity" size={84} />
                <ScoreRing value={scores.strategic_fit_score} label="Strategic" size={84} />
                <ScoreRing value={scores.execution_readiness_score} label="Execution" size={84} />
                <ScoreRing value={scores.investment_readiness_score} label="Investment" size={84} />
                <ScoreRing value={scores.regulatory_readiness_score} label="Regulatory" size={84} />
                <ScoreRing value={scores.capital_attractiveness_score} label="Capital" size={84} />
              </div>
            </div>

            {/* Content */}
            <div className="pt-6 sm:pt-8 min-h-[400px]">
              {section === "overview" && (
                <div className="space-y-6 sm:space-y-8">
                  <OISCard assessmentId={a.id} />
                  {memo.verdict && (
                    <div className="bg-card border border-border rounded-2xl p-6 sm:p-8 card-elev" data-testid="verdict-card">
                      <div className="flex items-center justify-between mb-3">
                        <div className="text-overline text-muted-foreground">Verdict</div>
                        <div className={`inline-flex items-center gap-1.5 text-xs font-bold uppercase tracking-wider px-3 py-1.5 rounded-full ${memo.verdict?.toLowerCase().includes("pursue") && !memo.verdict?.toLowerCase().includes("caution") ? "bg-teal-soft text-teal" : memo.verdict?.toLowerCase().includes("caution") ? "bg-gold-soft text-gold" : "bg-secondary text-muted-foreground"}`}>
                          {memo.verdict}
                        </div>
                      </div>
                      <p className="text-sm sm:text-base text-muted-foreground leading-relaxed">{memo.verdict_rationale}</p>
                    </div>
                  )}
                  {memo.executive_summary && (
                    <div data-testid="exec-summary">
                      <div className="text-overline text-muted-foreground mb-3">Executive summary</div>
                      <p className="text-base leading-relaxed">{memo.executive_summary}</p>
                    </div>
                  )}
                  {memo.strategic_recommendations && (
                    <div data-testid="recommendations" className="bg-card border border-border rounded-2xl p-6 sm:p-8 card-elev">
                      <div className="text-overline text-muted-foreground mb-4">Strategic recommendations</div>
                      <ul className="space-y-3">
                        {memo.strategic_recommendations.map((r, i) => (
                          <li key={i} className="flex gap-3 sm:gap-4 border-t border-border pt-3 first:border-t-0 first:pt-0">
                            <span className="text-primary font-mono text-xs font-bold mt-1 flex-shrink-0">{String(i+1).padStart(2,"0")}</span>
                            <span className="text-sm leading-relaxed">{r}</span>
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}
                </div>
              )}

              {section === "ois" && <OISCard assessmentId={a.id} />}

              {section === "quality" && <InstitutionalQualityPanel assessmentId={a.id} />}

              {section === "committee" && <InvestmentCommitteeTab assessmentId={a.id} />}

              {section === "sources" && <DataSourcesSection />}

              {section === "ecosystem" && (
                <div className="space-y-6">
                  <RelationshipGraph nodes={a.dossier?.ecosystem?.graph_nodes || []} links={a.dossier?.ecosystem?.graph_links || []} />
                  <DossierSections data={{ ecosystem: a.dossier?.ecosystem }} only="ecosystem" hideGraphFields />
                </div>
              )}

              {section !== "overview" && section !== "ois" && section !== "quality" && section !== "committee" && section !== "sources" && section !== "ecosystem" && (
                <DossierSections data={a.dossier} only={section} />
              )}
            </div>
          </div>

          {/* Right column: scores + copilot (desktop only — mobile uses bottom sheet) */}
          <div className="hidden lg:block space-y-6">
            <div className="bg-card border border-border rounded-2xl p-6 card-elev" data-testid="scores-panel">
              <div className="text-overline text-muted-foreground mb-5">Opportunity Qualification</div>
              <div className="grid grid-cols-2 gap-5">
                <ScoreRing value={scores.opportunity_score} label="Opportunity" size={104} />
                <ScoreRing value={scores.strategic_fit_score} label="Strategic Fit" size={104} />
                <ScoreRing value={scores.execution_readiness_score} label="Execution" size={104} />
                <ScoreRing value={scores.investment_readiness_score} label="Investment" size={104} />
                <ScoreRing value={scores.regulatory_readiness_score} label="Regulatory" size={104} />
                <ScoreRing value={scores.capital_attractiveness_score} label="Capital" size={104} />
              </div>
            </div>

            <div className="h-[640px] bg-card border border-border rounded-2xl overflow-hidden card-elev">
              <CopilotChat assessmentId={a.id} />
            </div>

            <a href="/book" className="block bg-gradient-to-br from-primary to-[hsl(var(--info))] text-primary-foreground rounded-2xl p-5 hover:shadow-lg transition-shadow card-elev" data-testid="book-demo-cta">
              <div className="text-[10px] uppercase tracking-[0.18em] font-bold opacity-80 mb-1">Product demo</div>
              <div className="font-display text-lg leading-tight tracking-tight">See a live walkthrough of the platform →</div>
              <div className="text-xs opacity-90 mt-1">30-minute software demo with our team</div>
            </a>
          </div>
        </div>
      )}

      {/* Mobile Copilot FAB */}
      {isReady && (
        <button
          onClick={() => setCopilotOpen(true)}
          className="lg:hidden fixed bottom-24 right-4 z-30 h-14 w-14 rounded-full bg-primary text-primary-foreground grid place-items-center card-elev-lg active:scale-95 transition-transform"
          aria-label="Open Copilot"
          data-testid="copilot-fab"
        >
          <ChatCircleDots size={22} weight="bold" />
        </button>
      )}

      {/* Mobile Copilot Bottom Sheet */}
      {copilotOpen && (
        <div className="lg:hidden fixed inset-0 z-50 flex items-end" data-testid="copilot-sheet">
          <div className="absolute inset-0 bg-foreground/40" onClick={() => setCopilotOpen(false)} />
          <div className="relative w-full h-[85vh] bg-card rounded-t-2xl overflow-hidden animate-in slide-in-from-bottom duration-200">
            <CopilotChat assessmentId={a.id} onClose={() => setCopilotOpen(false)} />
          </div>
        </div>
      )}
    </div>
  );
}
